import React, { useCallback, useEffect, useRef, useState } from "react";
import { Delete, PhotoCamera } from "@mui/icons-material";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import ConfirmationAlert from "../ConfirmationAlert";
import CarouselImage from "./CarouselImage";
import TimerProjectSelectionModal from "../Timer/TimerProjectSelectionModal";
import { useTimerContext } from "../../contexts/TimerContext";
import CarouselIcon from '../../assets/ic_default_carousel.svg?react';
import LeftArrow from '../../assets/ic_left_arrow.svg?react';
import RightArrow from '../../assets/ic_right_arrow.svg?react';

export interface ImageArrayType {
  photo_id: number;
  photo_path: string;
  isUploadedFromWebPortal: number;
}

interface CameraViewProps {
  images: ImageArrayType[];
  addImage: (image: File) => void;
  deleteImage: (activeSlide: number) => void;
  material_status: boolean;
  isLoading: boolean;
}

export interface ImageType {
  filename: string;
  base64File: string;
}

const CameraView: React.FC<CameraViewProps> = ({
  images,
  addImage,
  deleteImage,
  material_status,
  isLoading,
}) => {
  const theme = useTheme();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDeleteConfirmationAlert, setShowDeleteConfirmationAlert] =
    useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProjectPopup, setIsProjectPopup] = useState(false)
  const { selectedProjectForLog, selectedProjectIdForNavigation } = useTimerContext();
  const iconColor = theme.palette.primary.main;

  const openCamera = () => {
    if (selectedProjectForLog.id === null || (selectedProjectForLog.id !== null && selectedProjectForLog.id !== selectedProjectIdForNavigation)) {
      setIsProjectPopup(true)
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      addImage(files[0]);
      event.target.value = "";
    }
  };

  const handleDeleteImage = () => {
    // Implementation for deleting an image
    setShowDeleteConfirmationAlert(true);
  };

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [images]);

  const handlePrev = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : images.length - 1
    );
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex < images.length - 1 ? prevIndex + 1 : 0
    );
  }, [images.length]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.secondary.main,
            height: 400,
            fontSize: "18px",
          }}
        >
          Geen afbeelding beschikbaar
        </Box>
      ) : images.length > 0 ? (
        <Box sx={{ flex: 1, }}>
          <Box
            ref={imageContainerRef}
            sx={{
              position: "relative",
              overflow: "hidden",
              height: 400,
              alignItems: "center"
            }}
          >
            <CarouselImage
              key={images[currentImageIndex]?.photo_id}
              src={images[currentImageIndex]?.photo_path}
              alt={images[currentImageIndex]?.photo_id.toString()}
            />
            {currentImageIndex > 0 && (
              <Button
                onClick={handlePrev}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "45%",
                }}
                disableElevation
              >
                <LeftArrow style={{ width: 18, height: "100%", color: iconColor }} />
              </Button>
            )}
            {currentImageIndex < images.length - 1 && (
              <Button
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "45%",
                }}
                disableElevation
              >
                <RightArrow style={{ width: 18, height: "100%", color: iconColor }} />
              </Button>
            )}
          </Box>

          {!material_status && (
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}
            >
              <IconButton onClick={openCamera}>
                <PhotoCamera sx={{ color: "primary.main" }} fontSize="large" />
              </IconButton>
              <IconButton onClick={handleDeleteImage}>
                <Delete sx={{ color: "primary.main" }} fontSize="large" />
              </IconButton>
            </Box>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            capture="environment"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
          }}
        >
          <Button
            onClick={openCamera}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "secondary.main",
            }}
          >
            <CarouselIcon style={{ width: "100%", marginBottom: 1, color: iconColor }} />
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            capture="environment"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
        </Box>
      )}
      <ConfirmationAlert
        onDelete={() => deleteImage(currentImageIndex)}
        message={"Are you sure want to delete this image?"}
        open={showDeleteConfirmationAlert}
        handleClose={() => setShowDeleteConfirmationAlert(false)}
      />
      {isProjectPopup &&
        <TimerProjectSelectionModal
          handleClose={() => {
            setIsProjectPopup(false)
          }}
          isAnotherProject={selectedProjectForLog.id !== null && selectedProjectForLog.id !== selectedProjectIdForNavigation}
          isVisible={isProjectPopup}
          isDropdownDiabled={true}
        />}
    </Box>
  );
};

export default CameraView;
